import { useState, useMemo } from 'react';
import { Plus, Minus } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import {
  Button,
  Table,
  Card,
  Typography,
  Space,
  theme,
  type GlobalToken,
  type TableColumnsType,
} from 'antd';
import Tag from './Tag';
import type { Finding } from './types';
import { isCodeSnippet } from './utils/textFormatting';

const { Text, Paragraph } = Typography;

const verdictOrder: Record<string, number> = {
  VULNERABLE: 0,
  SECURE: 1,
  NOT_APPLICABLE: 2,
};

const severityOrder: Record<string, number> = {
  CRITICAL: 0,
  HIGH: 1,
  MEDIUM: 2,
  LOW: 3,
};

const confidenceOrder: Record<string, number> = {
  high: 0,
  medium: 1,
  low: 2,
};

const getSeverityTagClassName = (severity: string): string => {
  switch (severity) {
    case 'CRITICAL':
      return 'bg-[#fff1f0] dark:bg-[#3d1a1a] text-[#a8071a] dark:text-[#ff6b6b] border-[#ffa39e] dark:border-[#5a2020]';
    case 'HIGH':
      return 'bg-[#fff1f0] dark:bg-[#2c1618] text-[#cf1322] dark:text-[#ff7875] border-[#ffa39e] dark:border-[#5a2829]';
    case 'MEDIUM':
      return 'bg-[#fff7e6] dark:bg-[#2e2111] text-[#d46b08] dark:text-[#ffa940] border-[#ffd591] dark:border-[#4d3819]';
    case 'LOW':
      return 'bg-[#f6ffed] dark:bg-[#162312] text-[#389e0d] dark:text-[#73d13d] border-[#b7eb8f] dark:border-[#274916]';
    default:
      return 'bg-[#f5f5f5] dark:bg-[#2a2a2a] text-[#595959] dark:text-[#999999] border-[#d9d9d9] dark:border-[#434343]';
  }
};

// Confidence is an intensity ramp on a single hue (purple), NOT a
// red/orange/blue scale — it must read as a different axis from severity, or
// a low-confidence tag looks like a high-severity one. --ant-purple is safe
// here: the tags render inside the AntD Table subtree, where preset CSS vars
// resolve; purple has no semantic token equivalent.
const confidenceRampStyle = (confidence: string, token: GlobalToken): React.CSSProperties => {
  switch (confidence.toLowerCase()) {
    case 'high':
      return { color: 'var(--ant-purple)', background: token.colorFillTertiary, borderColor: 'transparent' };
    case 'medium':
      return {
        color: 'var(--ant-purple)',
        background: token.colorFillTertiary,
        borderColor: 'transparent',
        opacity: 0.55,
      };
    case 'low':
      return {
        color: 'var(--ant-purple)',
        background: 'transparent',
        borderColor: 'var(--ant-purple)',
        borderStyle: 'dashed',
        opacity: 0.7,
      };
    default:
      return { color: token.colorTextTertiary, background: token.colorFillTertiary, borderColor: 'transparent' };
  }
};

// Evidence match-type provenance: how the evidence was found ranks its
// weight (AST/runtime proof > pattern/config match > advisory > manual note).
const matchTypeStyle = (matchType: string, token: GlobalToken): React.CSSProperties => {
  const base: React.CSSProperties = { borderColor: 'transparent', margin: 0 };
  switch (matchType.toLowerCase()) {
    case 'ast':
    case 'runtime':
      return { ...base, color: token.colorError, background: token.colorErrorBg };
    case 'pattern':
    case 'config':
      return { ...base, color: token.colorWarning, background: token.colorWarningBg };
    case 'advisory':
      return { ...base, color: token.colorInfo, background: token.colorInfoBg };
    case 'manual':
      return { ...base, color: 'var(--ant-purple)', background: token.colorFillTertiary };
    default:
      return { ...base, color: token.colorTextTertiary, background: token.colorFillTertiary };
  }
};

const ExpandedContent = ({ finding }: { finding: Finding }) => {
  const { token } = theme.useToken();
  const isVulnerable = finding.finding === 'VULNERABLE';

  return (
    <div className="px-6 py-6">
      <Space direction="vertical" size="large" className="w-full">
        {finding.why_rule_matters && (
          <div>
            <Text className="text-base font-semibold block mb-2 leading-6 !text-theme-heading">
              Why This Matters
            </Text>
            <Paragraph className="text-sm font-normal leading-[22px] !mb-0 !text-theme-text">
              {finding.why_rule_matters}
            </Paragraph>
          </div>
        )}

        {finding.remediation && finding.remediation.length > 0 && (
          <div
            // A confirmed-vulnerable finding's fix guidance is the one thing
            // the reader must not scroll past — escalate it visually.
            style={
              isVulnerable
                ? {
                    border: `1px solid ${token.colorError}`,
                    background: token.colorErrorBg,
                    borderRadius: token.borderRadius,
                    padding: `${token.paddingSM}px ${token.padding}px`,
                  }
                : undefined
            }
          >
            <Text className="text-base font-semibold block mb-2 leading-6 !text-theme-heading">
              Remediation Guidance
            </Text>
            <ul className="list-disc list-inside space-y-1">
              {finding.remediation.map((item, remIdx) => (
                <li key={remIdx} className="text-sm font-normal leading-[22px] text-theme-text">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <Text className="text-sm font-bold block mb-2 leading-[22px] !text-theme-heading">
            Explanation
          </Text>
          <Paragraph className="text-sm font-normal leading-[22px] !mb-0 !text-theme-text">
            {finding.rationale}
          </Paragraph>
        </div>

        {finding.vulnerability_details && (
          <div>
            <Text className="text-base font-semibold block mb-2 leading-6 !text-theme-heading">
              Vulnerability Details
            </Text>
            <Paragraph className="text-sm font-normal leading-[22px] !mb-0 !text-theme-text">
              {finding.vulnerability_details}
            </Paragraph>
          </div>
        )}

        {finding.false_positive_check && (
          <div>
            <Text className="text-base font-semibold block mb-2 leading-6 !text-theme-heading">
              False-Positive Check
            </Text>
            <Paragraph className="text-sm font-normal leading-[22px] !mb-0 !text-theme-text">
              {finding.false_positive_check}
            </Paragraph>
          </div>
        )}

        {finding.evidence && finding.evidence.length > 0 && (
          <div>
            <Text className="text-base font-semibold block mb-2 leading-6 !text-theme-heading">
              Evidence ({finding.evidence.length})
            </Text>
            <Space direction="vertical" size="middle" className="w-full">
              {finding.evidence.map((ev, evIdx) => (
                <Card key={evIdx} bordered classNames={{ body: 'p-4' }}>
                  <Space direction="vertical" size="middle" className="w-full">
                    {/* One-line header: path + line range + provenance chip.
                        A finding often carries several evidence cards; the
                        stacked Path/Lines/Match-Type label rows tripled the
                        vertical cost of each one. */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <Text className="text-sm font-normal leading-[22px] font-mono !text-theme-text">
                        {ev.path}
                      </Text>
                      <Text
                        className="font-mono"
                        style={{ fontSize: token.fontSizeSM, color: token.colorTextTertiary }}
                      >
                        L{ev.start_line}
                        {ev.end_line !== ev.start_line ? `–${ev.end_line}` : ''}
                      </Text>
                      {ev.match_type && (
                        <Tag name={ev.match_type} style={matchTypeStyle(ev.match_type, token)} />
                      )}
                    </div>
                    <div>
                      {isCodeSnippet(ev.snippet) ? (
                        <pre className="m-0 p-3 border rounded text-xs font-mono whitespace-pre overflow-x-auto bg-theme-bg border-theme-border">
                          <code className="text-[#374151] dark:text-[#d4d4d4]">
                            {ev.snippet}
                          </code>
                        </pre>
                      ) : (
                        <div className="break-words">
                          <ReactMarkdown
                            components={{
                              p: ({ children }) => (
                                <Paragraph className="!mb-2 last:!mb-0 !text-sm !text-theme-text">{children}</Paragraph>
                              ),
                              strong: ({ children }) => <Text strong className="!text-sm !text-theme-heading">{children}</Text>,
                              code: ({ children, className }) => {
                                const match = /language-(\w+)/.exec(className || '');
                                const isInline = !className || !match;
                                return isInline ? (
                                  <Text
                                    code
                                    className="!px-1.5 !py-0.5 !rounded !text-xs !font-mono bg-theme-bg !text-theme-text"
                                  >
                                    {children}
                                  </Text>
                                ) : (
                                  <pre className="m-0 p-3 border rounded text-xs font-mono whitespace-pre overflow-x-auto bg-theme-bg border-theme-border">
                                    <code className="!text-theme-text">
                                      {String(children).replace(/\n$/, '')}
                                    </code>
                                  </pre>
                                );
                              },
                              a: ({ href, children }) => (
                                <a
                                  href={href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline break-all"
                                >
                                  {children}
                                </a>
                              ),
                              ul: ({ children }) => (
                                <ul className="list-disc list-inside mb-2 space-y-1 text-theme-text">{children}</ul>
                              ),
                              ol: ({ children }) => (
                                <ol className="list-decimal list-inside mb-2 space-y-1 text-theme-text">{children}</ol>
                              ),
                              li: ({ children }) => <li className="ml-2 text-theme-text">{children}</li>,
                            }}
                          >
                            {ev.snippet}
                          </ReactMarkdown>
                        </div>
                      )}
                    </div>
                  </Space>
                </Card>
              ))}
            </Space>
          </div>
        )}

        {finding.framework_mapping && (
          <div>
            <Text className="text-base font-semibold block mb-2 leading-6 !text-theme-heading">
              Frameworks
            </Text>
            <Space wrap size="small">
              {Object.entries(finding.framework_mapping).map(
                ([framework, values]) =>
                  values &&
                  values.length > 0 &&
                  values.map((val, valIdx) => (
                    <Tag
                      key={`${framework}-${valIdx}`}
                      name={`${framework}: ${val}`}
                      className="bg-theme-surface text-theme-text border-theme-border"
                    />
                  ))
              )}
            </Space>
          </div>
        )}
      </Space>
    </div>
  );
};

interface FindingsTableProps {
  findings: Finding[];
}

const FindingsTable = ({ findings }: FindingsTableProps) => {
  const { token } = theme.useToken();
  const [expandedRowKeys, setExpandedRowKeys] = useState<readonly React.Key[]>([]);

  const sortedFindings = useMemo(() => {
    return [...findings].sort(
      (a, b) => (verdictOrder[a.finding] ?? 999) - (verdictOrder[b.finding] ?? 999)
    );
  }, [findings]);

  const allExpanded =
    sortedFindings.length > 0 && expandedRowKeys.length === sortedFindings.length;

  const columns: TableColumnsType<Finding> = [
    {
      title: 'Verdict',
      dataIndex: 'finding',
      key: 'verdict',
      sorter: (a, b) => (verdictOrder[a.finding] || 999) - (verdictOrder[b.finding] || 999),
      render: (finding: string) => {
        if (finding === 'VULNERABLE') {
          return (
            <Tag
              name="VULNERABLE"
              className="bg-[#fff1f0] dark:bg-[#2c1618] text-[#cf1322] dark:text-[#ff7875] border-[#ffa39e] dark:border-[#5a2829]"
            />
          );
        } else if (finding === 'SECURE') {
          return (
            <Tag
              name="SECURE"
              className="bg-[#f6ffed] dark:bg-[#162312] text-[#389e0d] dark:text-[#73d13d] border-[#b7eb8f] dark:border-[#274916]"
            />
          );
        } else {
          return (
            <Tag
              name="NOT APPLICABLE"
              className="bg-[#f5f5f5] dark:bg-[#2a2a2a] text-[#595959] dark:text-[#999999] border-[#d9d9d9] dark:border-[#434343]"
            />
          );
        }
      },
      className: 'px-6 py-3',
    },
    {
      title: 'Rule Name',
      dataIndex: 'rule_name',
      key: 'rule_name',
      sorter: (a, b) => a.rule_name.localeCompare(b.rule_name),
      render: (ruleName: string, record) => (
        <div>
          <div className="text-sm">{ruleName}</div>
          {record.rule_id && (
            <div
              className="font-mono"
              style={{ fontSize: token.fontSizeSM, color: token.colorTextTertiary }}
            >
              {record.rule_id}
            </div>
          )}
        </div>
      ),
      className: 'px-6 py-3',
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      sorter: (a, b) => (severityOrder[a.severity] || 999) - (severityOrder[b.severity] || 999),
      render: (severity: string) => (
        <Tag name={severity} className={getSeverityTagClassName(severity)} />
      ),
      className: 'px-6 py-3 whitespace-nowrap',
    },
    {
      title: 'Confidence',
      dataIndex: 'confidence',
      key: 'confidence',
      sorter: (a, b) => (confidenceOrder[a.confidence] || 999) - (confidenceOrder[b.confidence] || 999),
      render: (confidence: string) => (
        <Tag name={confidence.toUpperCase()} style={confidenceRampStyle(confidence, token)} />
      ),
      className: 'px-6 py-3 whitespace-nowrap',
    },
    {
      title: 'CWEs',
      dataIndex: 'cwe',
      key: 'cwe',
      sorter: (a, b) => {
        const aCwe = a.cwe.length > 0 ? a.cwe[0] : '';
        const bCwe = b.cwe.length > 0 ? b.cwe[0] : '';
        return aCwe.localeCompare(bCwe);
      },
      render: (cwe: string[]) => (
        <div className="flex flex-wrap gap-1">
          {cwe.map((item, idx) => (
            <Tag
              key={idx}
              name={item}
              className="bg-[#f5f5f5] dark:bg-[#2a2a2a] text-[#000000] dark:text-white border-[#d9d9d9] dark:border-[#434343]"
            />
          ))}
        </div>
      ),
      className: 'px-6 py-3 whitespace-nowrap',
    },
  ];

  // No color overrides here: header, row hover, borders, and expanded-row
  // backgrounds come from the consumer's AntD ConfigProvider theme, so the
  // table matches every other table in the host app.
  return (
    <div>
      {sortedFindings.length > 0 && (
        <div className="flex justify-end mb-1">
          <Button
            type="link"
            size="small"
            onClick={() =>
              setExpandedRowKeys(allExpanded ? [] : sortedFindings.map((_, idx) => idx))
            }
          >
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </Button>
        </div>
      )}
      <Table
        columns={columns}
        dataSource={sortedFindings.map((finding, index) => ({ ...finding, key: index }))}
        pagination={false}
        expandable={{
          expandedRowKeys: expandedRowKeys as React.Key[],
          onExpandedRowsChange: (keys) => {
            setExpandedRowKeys(keys);
          },
          expandedRowRender: (finding) => <ExpandedContent finding={finding} />,
          expandIcon: ({ expanded, onExpand, record }) => (
            <button
              onClick={(e) => onExpand(record, e)}
              className="px-3 py-4 text-center"
              type="button"
            >
              {expanded ? (
                <Minus className="w-4 h-4 text-theme-muted" />
              ) : (
                <Plus className="w-4 h-4 text-theme-muted" />
              )}
            </button>
          ),
          columnWidth: 48,
        }}
        rowClassName="cursor-pointer"
      />
    </div>
  );
};

export default FindingsTable;
